import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Camera, FileText } from 'lucide-react';
import StoreLayout, { PlaceholderImage, PriceDisplay, resolveImageSrc } from '../../components/store/StoreLayout';
import StickyFooterNav from '../../components/store/StickyFooterNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StoreHome() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/store/products`)
      .then(r => setProducts(r.data || []))
      .finally(() => setLoading(false));
    axios.get(`${BACKEND_URL}/api/store/categories`)
      .then(r => setCategories((r.data || []).slice(0, 8)))
      .catch(() => setCategories([]));
  }, []);

  const featured = products.slice(0, 8);
  const newArrivals = products.slice(8, 16);

  return (
    <StoreLayout>
      <StickyFooterNav />

      {/* Slim top strip — categories */}
      {categories.length > 0 && (
        <section className="border-b border-stone-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="home-category-strip">
            <span className="text-[11px] uppercase tracking-widest text-stone-400 mr-2 flex-shrink-0">Shop by brand</span>
            {categories.map(c => (
              <Link
                key={c.brand}
                to={`/shop?cat=${encodeURIComponent(c.brand)}`}
                className="text-xs whitespace-nowrap bg-stone-100 hover:bg-amber-100 hover:text-amber-800 text-stone-700 px-3 py-1.5 rounded-full transition"
              >
                {c.brand} <span className="text-[10px] opacity-70 ml-0.5">({c.count})</span>
              </Link>
            ))}
            <Link to="/shop" className="text-xs whitespace-nowrap text-amber-800 hover:underline ml-2 flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </section>
      )}

      {/* Featured — product-first, no hero */}
      <section className="max-w-7xl mx-auto px-4 pt-10 pb-8">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-stone-900">Shop the collection</h1>
            <p className="text-sm text-stone-500 mt-1">Interior hardware, delivered directly from trusted vendors.</p>
          </div>
          <Link to="/shop" className="text-sm text-amber-800 hover:text-amber-900 flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-square bg-stone-100 rounded-xl" />
                <div className="h-3 bg-stone-100 rounded mt-3 w-3/4" />
                <div className="h-3 bg-stone-100 rounded mt-2 w-1/2" />
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="text-center py-16 text-stone-500 text-sm border border-dashed border-stone-200 rounded-lg" data-testid="home-empty-state">
            Our store is being stocked. Please check back shortly, or reach out on WhatsApp for an instant quote.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-featured-grid">
            {featured.map(p => <ProductCard key={p.product_id} p={p} />)}
          </div>
        )}
      </section>

      {/* Secondary CTAs — kept subtle */}
      <section className="max-w-7xl mx-auto px-4 py-6 grid md:grid-cols-2 gap-3">
        <Link to="/portfolio" className="group border border-stone-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-amber-500 hover:bg-amber-50/40 transition" data-testid="home-portfolio-cta">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-stone-900 text-white flex items-center justify-center">
              <Camera className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-medium text-stone-900">See our interior work</div>
              <div className="text-xs text-stone-500">Real homes we&apos;ve delivered end-to-end.</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-amber-700 group-hover:translate-x-1 transition" />
        </Link>
        <Link to="/get-quote" className="group border border-stone-200 rounded-xl px-5 py-4 flex items-center justify-between hover:border-amber-500 hover:bg-amber-50/40 transition" data-testid="home-quote-cta">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-700 text-white flex items-center justify-center">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <div className="text-sm font-medium text-stone-900">Need a custom quote?</div>
              <div className="text-xs text-stone-500">Modular kitchens, full-home interiors, bespoke joinery.</div>
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-stone-400 group-hover:text-amber-700 group-hover:translate-x-1 transition" />
        </Link>
      </section>

      {/* New arrivals (if we have more than 8 published products) */}
      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-stone-900">More products</h2>
            <Link to="/shop" className="text-sm text-amber-800 hover:text-amber-900 flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="home-more-grid">
            {newArrivals.map(p => <ProductCard key={p.product_id} p={p} />)}
          </div>
        </section>
      )}
    </StoreLayout>
  );
}

function ProductCard({ p }) {
  return (
    <Link to={`/product/${p.product_id}`} className="group block" data-testid={`featured-${p.product_id}`}>
      <div className="aspect-square rounded-xl overflow-hidden bg-stone-50 border border-stone-200 group-hover:border-amber-500 transition">
        {p.image_urls && p.image_urls[0]
          ? <img src={resolveImageSrc(p.image_urls[0])} alt={p.product_name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" loading="lazy" />
          : <PlaceholderImage className="w-full h-full" />}
      </div>
      <div className="mt-2">
        <div className="text-[11px] text-stone-500 uppercase tracking-widest">{p.product_brand}</div>
        <div className="text-sm font-medium text-stone-800 line-clamp-2 min-h-[2.5rem]">{p.product_name}</div>
        <div className="mt-1">
          <PriceDisplay mrp={p.mrp} selling={p.selling_price} size="sm" />
        </div>
      </div>
    </Link>
  );
}
